import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mulesoft/anypoint-components/lib/Button';
import BasicInformationSection from './BasicInformationSection';
import ScheduledRunSection from './ScheduledRunSection';
import ConnectionSection from './ConnectionSection';
import CustomScheduleModal from './CustomScheduleModal';
import { useScannerForm } from './hooks';
import styles from './ScannerConfigurationForm.css';

/**
 * ScannerConfigurationForm Component
 *
 * A comprehensive form for configuring AI Agent Scanners with support for:
 * - Multiple target systems (Amazon Bedrock, Azure OpenAI, Google Vertex AI)
 * - Multiple authentication methods (API key, OAuth)
 * - Conditional field rendering based on authentication method
 * - Advanced settings (run frequency, timeout, retry attempts)
 * - Form validation based on required fields
 * - Both add and edit modes
 */
const ScannerConfigurationForm = ({
  onSubmit,
  onCancel,
  initialData,
  mode,
  organizationId,

  organizationName,
  ownerId,
  isSubmitting,
  nameError,
  resetNameError,
  context
}) => {
  // Build context for API payload transformation
  const formContext = {
    organizationId,
    ownerId,
    businessGroup: organizationId // Using orgId as businessGroup
  };

  const {
    // State
    formData,
    connectionStatus,
    connectionError,
    connectionHeaderError,
    showCustomModal,
    showAdvancedSettings,
    customSchedule,
    isFormValid,
    scheduleStatus,

    // Handlers
    handleChange,
    handleSubmit,
    handleTestConnection,
    handleCustomScheduleSave,
    handleCustomScheduleCancel,
    handleCustomScheduleChange,
    toggleAdvancedSettings,
    handleEmailNotificationsToggle,
    handleAddEmail,
    handleRemoveEmail,
    handleScheduleStatusToggle
  } = useScannerForm(initialData, mode, onSubmit, formContext, resetNameError);

  const subtitle =
    mode === 'edit'
      ? 'Update the scanner configuration and authentication settings.'
      : `Add a scanner to discover agents in external systems & import them into your current business group${organizationName ? ` ${organizationName}` : ''}.`;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>
          {mode === 'edit' ? 'Edit Scanner' : 'Add Scanner'}
        </h2>
        <p className={styles.subtitle}>{subtitle}</p>
      </header>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* General Details Section */}
        <BasicInformationSection
          name={formData.name}
          description={formData.description}
          onChange={handleChange}
          nameError={nameError}
        />

        {/* Scheduled run configuration Section */}
        <ScheduledRunSection
          status={scheduleStatus}
          runFrequency={formData.runFrequency}
          at={formData.at}
          syncReview={formData.syncReview}
          onChange={handleChange}
          onStatusToggle={handleScheduleStatusToggle}
          mode={mode}
        />

        {/* Connection Configuration Section */}
        <ConnectionSection
          mode={mode}
          formData={formData}
          connectionStatus={connectionStatus}
          connectionError={connectionError}
          connectionHeaderError={connectionHeaderError}
          showAdvancedSettings={showAdvancedSettings}
          onChange={handleChange}
          onTestConnection={handleTestConnection}
          onToggleAdvancedSettings={toggleAdvancedSettings}
          onToggleEmailNotifications={handleEmailNotificationsToggle}
          onAddEmail={handleAddEmail}
          onRemoveEmail={handleRemoveEmail}
          context={context}
        />

        {/* Form Actions */}
        <div className={styles['form-actions']}>
          <Button
            type="button"
            kind="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
            testId="cancel-scanner-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            kind="primary"
            disabled={!isFormValid || isSubmitting}
            testId="submit-scanner-button"
          >
            {isSubmitting && 'Saving...'}
            {!isSubmitting && mode === 'edit' && 'Update Scanner'}
            {!isSubmitting && mode !== 'edit' && 'Add Scanner'}
          </Button>
        </div>
      </form>
      {/* Required Field Legend */}
      <div className={styles['required-field-legend']}>
        <span className={styles.asterisk}>*</span> Indicates required field
      </div>

      {/* Custom Range Modal */}
      {showCustomModal && (
        <CustomScheduleModal
          customSchedule={customSchedule}
          onScheduleChange={handleCustomScheduleChange}
          onSave={handleCustomScheduleSave}
          onCancel={handleCustomScheduleCancel}
        />
      )}
    </div>
  );
};

ScannerConfigurationForm.propTypes = {
  /** Callback when form is submitted with scanner data */
  onSubmit: PropTypes.func,
  /** Callback when form is cancelled */
  onCancel: PropTypes.func,
  /** Organization ID from profile */
  organizationId: PropTypes.string,
  /** Organization name from profile (profile.organization.name) */
  organizationName: PropTypes.string,
  /** Owner ID (user ID) from profile */
  ownerId: PropTypes.string,
  /** Whether form submission is in progress */
  isSubmitting: PropTypes.bool,
  /** Flag for name error */
  nameError: PropTypes.bool,
  /** Callback when reset name field */
  resetNameError: PropTypes.func,
  /** Optional initial data for edit mode */
  initialData: PropTypes.shape({
    id: PropTypes.string,
    state: PropTypes.oneOf(['SCHEDULED', 'PAUSED', 'STOPPED']),
    name: PropTypes.string,
    description: PropTypes.string,
    targetSystem: PropTypes.string,
    authMethod: PropTypes.string,
    target: PropTypes.string,
    method: PropTypes.string,
    keyId: PropTypes.string,
    keySecret: PropTypes.string,
    region: PropTypes.string,
    clientId: PropTypes.string,
    clientSecret: PropTypes.string,
    authorizationServerURL: PropTypes.string,
    tokenEndpointURL: PropTypes.string,
    redirectURI: PropTypes.string,
    scope: PropTypes.string,
    runFrequency: PropTypes.string,
    at: PropTypes.string,
    syncReview: PropTypes.string,
    emailNotificationsEnabled: PropTypes.bool,
    recipientEmails: PropTypes.arrayOf(PropTypes.string),
    parameters: PropTypes.object,
    runPolicy: PropTypes.shape({
      runFrequency: PropTypes.string,
      at: PropTypes.string,
      syncReview: PropTypes.string
    }),
    advancedSettings: PropTypes.shape({
      runFrequency: PropTypes.string,
      at: PropTypes.string,
      syncReview: PropTypes.string,
      emailNotificationsEnabled: PropTypes.bool,
      recipientEmails: PropTypes.arrayOf(PropTypes.string)
    }),
    connection: PropTypes.shape({
      id: PropTypes.string,
      state: PropTypes.oneOf(['SCHEDULED', 'PAUSED', 'STOPPED']),
      targetSystem: PropTypes.shape({
        id: PropTypes.string
      }),
      authScheme: PropTypes.string,
      authParameters: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    }),
    config: PropTypes.object
  }),
  /** Form mode: 'add' or 'edit' */
  mode: PropTypes.oneOf(['add', 'edit']),
  /** Request context with authorization */
  context: PropTypes.object.isRequired
};

ScannerConfigurationForm.defaultProps = {
  onSubmit: () => {},
  onCancel: () => {},
  initialData: null,
  mode: 'add',
  organizationId: null,
  organizationName: null,
  ownerId: null,
  isSubmitting: false,
  nameError: false,
  resetNameError: () => {}
};

export default ScannerConfigurationForm;
